import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';

const currentYear = new Date().getFullYear();

export default function OwnerCit8() {
  const { currentCompany } = useApp();
  const [year, setYear] = useState(currentYear - 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  async function loadCit8() {
    if (!currentCompany?.id) { setMsg('❌ Nie wybrano firmy'); return; }
    setLoading(true); setMsg('');
    try {
      const { data: d } = await api.get(`/owner/tax/cit8?company_id=${currentCompany.id}&year=${year}`);
      setData(d);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }

  useEffect(() => { if (currentCompany?.id) loadCit8(); }, [currentCompany?.id, year]);

  async function downloadPdf() {
    try {
      const response = await api.get(
        `/owner/tax/cit8/pdf?company_id=${currentCompany.id}&year=${year}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CIT8_${year}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    }
  }

  const cardStyle = {
    background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 16, marginBottom: 12,
    border: '1px solid rgba(255,255,255,0.08)',
  };
  const rowStyle = { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' };

  return (
    <div className="page">
      <BackButton />
      <h1>🏛 Deklaracja CIT-8</h1>
      {msg && <div className="message" style={{ marginBottom: 12 }}>{msg}</div>}

      {/* Year selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontSize: 14, opacity: 0.7 }}>Rok podatkowy:</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {[currentYear - 2, currentYear - 1, currentYear].map(y => (
            <button key={y} onClick={() => setYear(y)} style={{
              padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
              background: year === y ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
              color: year === y ? '#818cf8' : 'rgba(255,255,255,0.6)',
            }}>
              {y}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, opacity: 0.5 }}>Obliczanie...</div>
      ) : data ? (
        <>
          {/* Company info */}
          <div style={cardStyle}>
            <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 4 }}>Podatnik</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{data.company_name}</div>
            <div style={{ fontSize: 13, opacity: 0.5 }}>NIP: {data.company_nip || '—'}</div>
          </div>

          {/* Main summary */}
          <div style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(99,102,241,0.04))',
            borderLeft: '4px solid #6366f1',
          }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 12 }}>📊 Rozliczenie roczne {year}</div>
            <div style={rowStyle}>
              <span style={{ opacity: 0.7 }}>Przychody</span>
              <span style={{ fontWeight: 700, color: '#10b981' }}>{data.total_income.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN</span>
            </div>
            <div style={rowStyle}>
              <span style={{ opacity: 0.7 }}>Koszty</span>
              <span style={{ fontWeight: 700, color: '#f87171' }}>{data.total_expenses.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN</span>
            </div>
            <div style={{ ...rowStyle, borderBottom: 'none', paddingTop: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{data.profit >= 0 ? 'Dochód' : 'Strata'}</span>
              <span style={{ fontWeight: 800, fontSize: 18, color: data.profit >= 0 ? '#10b981' : '#f87171' }}>
                {data.profit.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN
              </span>
            </div>
          </div>

          {/* Tax */}
          <div style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(245,158,11,0.04))',
            borderLeft: '4px solid #f59e0b',
          }}>
            <div style={rowStyle}>
              <span style={{ opacity: 0.7 }}>Stawka CIT</span>
              <span style={{ fontWeight: 700 }}>{data.cit_rate}%</span>
            </div>
            <div style={rowStyle}>
              <span style={{ opacity: 0.7, fontSize: 11 }}>
                {data.cit_rate === 9 ? '(mały podatnik / nowa firma)' : '(stawka standardowa)'}
              </span>
              <span></span>
            </div>
            <div style={{ ...rowStyle, borderBottom: 'none', paddingTop: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Podatek CIT należny</span>
              <span style={{ fontWeight: 800, fontSize: 20, color: '#f59e0b' }}>
                {data.cit_tax.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN
              </span>
            </div>
          </div>

          {/* Monthly breakdown */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 10 }}>📅 Rozkład miesięczny</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                    <th style={{ padding: 6, textAlign: 'left', opacity: 0.6 }}>Miesiąc</th>
                    <th style={{ padding: 6, textAlign: 'right', opacity: 0.6 }}>Przychody</th>
                    <th style={{ padding: 6, textAlign: 'right', opacity: 0.6 }}>Koszty</th>
                    <th style={{ padding: 6, textAlign: 'right', opacity: 0.6 }}>Wynik</th>
                  </tr>
                </thead>
                <tbody>
                  {data.monthly.map(m => {
                    const names = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];
                    return (
                      <tr key={m.month} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: 5 }}>{names[m.month - 1]}</td>
                        <td style={{ padding: 5, textAlign: 'right', color: '#10b981' }}>{m.income.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}</td>
                        <td style={{ padding: 5, textAlign: 'right', color: '#f87171' }}>{m.expenses.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}</td>
                        <td style={{ padding: 5, textAlign: 'right', fontWeight: 600, color: m.profit >= 0 ? '#10b981' : '#f87171' }}>
                          {m.profit.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Download PDF */}
          <button className="btn btn-primary" onClick={downloadPdf} style={{
            width: '100%', padding: 16, fontSize: 16, fontWeight: 800, borderRadius: 14, marginBottom: 20,
          }}>
            📄 Pobierz PDF CIT-8
          </button>
        </>
      ) : null}
    </div>
  );
}
